import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Modal } from "react-bootstrap";
import {
  FaPlay,
  FaRocket,
  FaMobile,
  FaMicrochip,
  FaRobot,
  FaGamepad,
  FaDownload,
  FaChevronDown,
  FaChevronUp,
  FaHeart,
  FaTrophy,
  FaLaptopCode,
  FaTimes,
} from "react-icons/fa";
import { GiBrain } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import "../../styles/kids.css";

// Import project images from local storage (update these paths with your actual images)
import spaceGameImg from "../../assets/images/kids/space-game.jpg";
import racingGameImg from "../../assets/images/kids/racing-game.jpg";
import smartRobotImg from "../../assets/images/kids/smart-robot.jpg";
import homeAutomationImg from "../../assets/images/kids/home-automation.jpg";
import foodAppImg from "../../assets/images/kids/food-app.jpg";
import galaxyShooterImg from "../../assets/images/kids/galaxy-shooter.jpg";

const Kids = () => {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [activePortfolioFilter, setActivePortfolioFilter] = useState("all");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [counters, setCounters] = useState({
    students: 0,
    projects: 0,
    mentors: 0,
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // Animated counters
    const animateCounter = (target, key) => {
      let start = 0;
      const increment = target / 50;
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setCounters((prev) => ({ ...prev, [key]: target }));
          clearInterval(timer);
        } else {
          setCounters((prev) => ({ ...prev, [key]: Math.floor(start) }));
        }
      }, 30);
    };

    animateCounter(500, "students");
    animateCounter(50, "projects");
    animateCounter(21, "mentors");
  }, []);

  const programs = [
    {
      icon: <FaMobile />,
      title: "App Development",
      color: "#4CAF50", // Green
      description:
        "Create your own Android apps using MIT App Inventor's drag-and-drop interface. No coding experience needed!",
      redirectPath: "/kids/app-development",
    },
    {
      icon: <FaMicrochip />,
      title: "Arduino & Electronics",
      color: "#0056b3", // Blue
      description:
        "Build exciting electronic projects and robots. Learn how sensors and motors work!",
      redirectPath: "/kids/arduino-electronics",
    },
    {
      icon: <FaRobot />,
      title: "Robotics for Kids",
      color: "#9C27B0", // Purple
      description:
        "Design, build, and program your own robots using LEGO Mindstorms and VEX Robotics!",
      redirectPath: "/kids/robotics-for-kids",
    },
    {
      icon: <FaGamepad />,
      title: "Game Development",
      color: "#FF9800", // Orange
      description:
        "Create awesome video games and learn coding through game design and storytelling!",
      redirectPath: "/kids/game-development",
    },
  ];

  const features = [
    {
      icon: <FaLaptopCode />,
      title: "Hands-on Learning",
      description: "Learn by doing with real projects and practical exercises",
    },
    {
      icon: <GiBrain />,
      title: "Creative Thinking",
      description: "Develop problem-solving and logical thinking skills",
    },
    {
      icon: <FaHeart />,
      title: "Fun Environment",
      description: "Engaging classes with games and interactive activities",
    },
    {
      icon: <FaTrophy />,
      title: "Certification",
      description: "Get recognized with completion certificates",
    },
  ];

  // Projects with local images - Update these paths with your actual images
  const projects = [
    {
      id: 1,
      title: "Space Adventure Game",
      category: "games",
      icon: "🎮",
      student: "Aarav, 12",
      description: "Scratch Platformer Game with multiple levels",
      image: spaceGameImg,
    },
    {
      id: 2,
      title: "Racing Champion",
      category: "games",
      icon: "🏎️",
      student: "Rohan, 11",
      description: "High-speed racing game with custom tracks",
      image: racingGameImg,
    },
    {
      id: 3,
      title: "Smart Robot",
      category: "robotics",
      icon: "🤖",
      student: "Diya, 10",
      description: "Line-following robot with sensors",
      image: smartRobotImg,
    },
    {
      id: 4,
      title: "Home Automation",
      category: "robotics",
      icon: "🏠",
      student: "Ananya, 14",
      description: "Control lights and fans with your phone",
      image: homeAutomationImg,
    },
    {
      id: 5,
      title: "Food Express App",
      category: "apps",
      icon: "🍕",
      student: "Krishna, 13",
      description: "Order food delivery app prototype",
      image: foodAppImg,
    },
    {
      id: 6,
      title: "Galaxy Shooter",
      category: "games",
      icon: "🌌",
      student: "Vihaan, 11",
      description: "Space shooter game with power-ups",
      image: galaxyShooterImg,
    },
  ];

  const faqs = [
    {
      q: "🎮 What age group is this suitable for?",
      a: "Our coding classes are designed for kids aged 12-18. We offer different levels based on age and experience, ensuring each child learns at their own pace.",
    },
    {
      q: "💻 Do kids need any prior experience?",
      a: "No prior experience is required! We offer beginner-friendly courses that introduce kids to the basics of coding, robotics, and game design.",
    },
    {
      q: "🖥️ What equipment does my child need?",
      a: "A computer or laptop with internet access is needed. For robotics classes, we provide all necessary kits and materials.",
    },
    {
      q: "🌍 Are there online classes available?",
      a: "Yes! We offer live online classes with real instructors, as well as in-person classroom training.",
    },
    {
      q: "🏆 Will my child get a certificate?",
      a: "Yes, every student receives a completion certificate at the end of the course.",
    },
    {
      q: "💰 Is there a demo class available?",
      a: "Yes! You can book a FREE demo class to experience our teaching methodology.",
    },
  ];

  const filteredProjects =
    activePortfolioFilter === "all"
      ? projects
      : projects.filter((p) => p.category === activePortfolioFilter);

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // Handle project card click - open modal with image
  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setShowProjectModal(true);
  };

  return (
    <div className="kids-page-fun">
      <PublicNavbar />

      {/* ========== HERO SECTION ========== */}
      <section className="kids-fun-hero">
        <div className="kids-fun-hero-bubbles">
          <div className="kids-fun-bubble"></div>
          <div className="kids-fun-bubble"></div>
          <div className="kids-fun-bubble"></div>
          <div className="kids-fun-bubble"></div>
          <div className="kids-fun-bubble"></div>
        </div>
        <Container>
          <Row className="align-items-center">
            <Col lg={7}>
              <div className="kids-fun-hero-content">
                <div className="kids-fun-hero-badge">
                  <span>🚀</span>
                  <span>Summer Camp 2026 Now Enrolling!</span>
                  <span>✨</span>
                </div>
                <h1 className="kids-fun-hero-title">
                  CODE, BUILD, and{" "}
                  <span className="kids-fun-highlight">CREATE</span>
                  <br />
                  THIS SUMMER!
                </h1>
                <p className="kids-fun-hero-description">
                  At Sensible Academy, we believe every child has the potential
                  to become the next great innovator. Join us for a summer of
                  coding, robotics, and game development fun!
                </p>
                <div className="kids-fun-hero-buttons">
                  <Button
                    className="kids-fun-btn-primary"
                    onClick={() => navigate("/courses")}
                  >
                    <FaRocket className="me-2" /> Explore Programs
                  </Button>
                  <Button
                    className="kids-fun-btn-outline"
                    onClick={() => setShowVideoModal(true)}
                  >
                    <FaPlay className="me-2" /> Watch Demo
                  </Button>
                </div>
                <div className="kids-fun-hero-stats">
                  <div className="kids-fun-stat">
                    <span className="kids-fun-stat-number">
                      {counters.students}+
                    </span>
                    <span className="kids-fun-stat-label">Happy Kids</span>
                  </div>
                  <div className="kids-fun-stat">
                    <span className="kids-fun-stat-number">
                      {counters.projects}+
                    </span>
                    <span className="kids-fun-stat-label">Projects Built</span>
                  </div>
                  <div className="kids-fun-stat">
                    <span className="kids-fun-stat-number">
                      {counters.mentors}+
                    </span>
                    <span className="kids-fun-stat-label">Expert Mentors</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={5}>
              <div className="kids-fun-hero-illustration">
                <div className="kids-fun-floating-icon kids-fun-icon-1">🤖</div>
                <div className="kids-fun-floating-icon kids-fun-icon-2">💻</div>
                <div className="kids-fun-floating-icon kids-fun-icon-3">🎮</div>
                <div className="kids-fun-floating-icon kids-fun-icon-4">📱</div>
                <div className="kids-fun-floating-icon kids-fun-icon-5">⚡</div>
                <div className="kids-fun-floating-icon kids-fun-icon-6">🎨</div>
                <img
                  src="https://cdn-icons-png.flaticon.com/512/16008/16008801.png"
                  alt="Kids Coding"
                  className="kids-fun-main-illustration"
                />
              </div>
            </Col>
          </Row>
        </Container>
        <div className="kids-fun-hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120">
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,96C960,107,1056,117,1152,112C1248,107,1344,85,1392,74.7L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* ========== FEATURES SECTION ========== */}
      <section className="kids-fun-features">
        <Container>
          <div className="kids-fun-section-header text-center">
            <h2>
              Why Kids <span className="kids-fun-highlight">Love Us</span> ❤️
            </h2>
            <p>Making learning fun and exciting for every child</p>
          </div>
          <Row className="g-4">
            {features.map((feature, index) => (
              <Col md={6} lg={3} key={index}>
                <div className="kids-fun-feature-card">
                  <div className="kids-fun-feature-icon">{feature.icon}</div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ========== PROGRAMS SECTION ========== */}
      <section className="kids-fun-programs">
        <Container>
          <div className="kids-fun-section-header text-center">
            <h2>
              Our Awesome <span className="kids-fun-highlight">Programs</span>{" "}
              🚀
            </h2>
            <p>Choose the perfect program for your child's interests</p>
          </div>
          <Row className="g-4">
            {programs.map((program, index) => (
              <Col md={6} lg={3} key={index}>
                <div className="kids-fun-program-card">
                  <div
                    className="kids-fun-program-icon"
                    style={{ background: program.color }}
                  >
                    {program.icon}
                  </div>
                  <h4>{program.title}</h4>
                  <p>{program.description}</p>
                  <Button
                    className="kids-fun-program-btn"
                    onClick={() => navigate(program.redirectPath)}
                  >
                    Learn More →
                  </Button>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ========== SUMMER CAMP SECTION ========== */}
      <section className="kids-fun-summer">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <div className="kids-fun-summer-badge">
                ☀️ Summer Special 2026 ☀️
              </div>
              <h2 className="kids-fun-summer-title">
                Summer Coding Camps & Workshops
              </h2>
              <p className="kids-fun-summer-description">
                Make your summer productive and fun! Our Summer Coding Camps and
                Workshops are perfect for kids of all ages. With hands-on
                activities in coding, robotics, and game development!
              </p>
              <div className="kids-fun-camp-options">
                <div className="kids-fun-camp-item">
                  <div className="kids-fun-camp-icon">⏰</div>
                  <div>
                    <h4>2-Week Intensive Camp</h4>
                    <p>Fast-track learning, Daily projects, Certificate</p>
                  </div>
                </div>
                <div className="kids-fun-camp-item">
                  <div className="kids-fun-camp-icon">📅</div>
                  <div>
                    <h4>1-Month Regular Camp</h4>
                    <p>Comprehensive program, Multiple projects, Certificate</p>
                  </div>
                </div>
                <div className="kids-fun-camp-item">
                  <div className="kids-fun-camp-icon">🌤️</div>
                  <div>
                    <h4>Weekend Workshops</h4>
                    <p>For busy schedules, Flexible timing, Certificate</p>
                  </div>
                </div>
              </div>
              <div className="kids-fun-camp-buttons">
                <Button
                  className="kids-fun-btn-primary"
                  onClick={() => navigate("/register")}
                >
                  Register Now →
                </Button>
                <Button className="kids-fun-btn-outline">
                  <FaDownload className="me-2" /> Download Brochure
                </Button>
              </div>
            </Col>
            <Col lg={6}>
              <div className="kids-fun-summer-illustration">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/2942/2942866.png"
                  alt="Summer Camp"
                />
                <div className="kids-fun-summer-floating">
                  <span>☀️</span>
                  <span>🏖️</span>
                  <span>🍦</span>
                  <span>🤖</span>
                  <span>🎮</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ========== PORTFOLIO SECTION ========== */}
      <section className="kids-fun-portfolio">
        <Container>
          <div className="kids-fun-section-header text-center">
            <h2>
              Our Young <span className="kids-fun-highlight">Creators</span> 🏆
            </h2>
            <p>Check out amazing projects made by our students</p>
          </div>

          <div className="kids-fun-portfolio-filters">
            <button
              className={activePortfolioFilter === "all" ? "active" : ""}
              onClick={() => setActivePortfolioFilter("all")}
            >
              ALL
            </button>
            <button
              className={activePortfolioFilter === "games" ? "active" : ""}
              onClick={() => setActivePortfolioFilter("games")}
            >
              🎮 GAMES
            </button>
            <button
              className={activePortfolioFilter === "robotics" ? "active" : ""}
              onClick={() => setActivePortfolioFilter("robotics")}
            >
              🤖 ROBOTICS
            </button>
            <button
              className={activePortfolioFilter === "apps" ? "active" : ""}
              onClick={() => setActivePortfolioFilter("apps")}
            >
              📱 APPS
            </button>
          </div>

          <Row className="g-4">
            {filteredProjects.map((project) => (
              <Col md={6} lg={4} key={project.id}>
                <div
                  className="kids-fun-portfolio-card"
                  onClick={() => handleProjectClick(project)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="kids-fun-portfolio-icon">{project.icon}</div>
                  <h4>{project.title}</h4>
                  <p>{project.description}</p>
                  <div className="kids-fun-portfolio-student">
                    ⭐ {project.student}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Note: View All Projects button - redirects to courses page since portfolio is already shown */}
          <div className="text-center mt-5">
            <Button
              className="kids-fun-btn-primary"
              onClick={() => navigate("/courses")}
            >
              View All Courses →
            </Button>
          </div>
        </Container>
      </section>

      {/* ========== FAQ SECTION ========== */}
      <section className="kids-fun-faq">
        <Container>
          <div className="kids-fun-section-header text-center">
            <h2>
              Parents Ask <span className="kids-fun-highlight">Questions</span>{" "}
              ❓
            </h2>
            <p>Everything you need to know about our kids programs</p>
          </div>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="kids-fun-faq-container">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`kids-fun-faq-item ${openFaqIndex === index ? "active" : ""}`}
                  >
                    <div
                      className="kids-fun-faq-question"
                      onClick={() => toggleFaq(index)}
                    >
                      <h4>{faq.q}</h4>
                      <span>
                        {openFaqIndex === index ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </span>
                    </div>
                    <div className="kids-fun-faq-answer">
                      <p>{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="kids-fun-faq-contact">
                <div className="kids-fun-contact-card">
                  <div className="kids-fun-contact-icon">🎧</div>
                  <h4>Still Have Questions?</h4>
                  <p>
                    Can't find the answer you're looking for? Please contact our
                    parent support team.
                  </p>
                  <div className="kids-fun-contact-links">
                    <a href="mailto:contact@sensibleacademy.com">
                      📧 contact@sensibleacademy.com
                    </a>
                    <a href="tel:+917722899444">📞 +91 77228-99444</a>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ========== CTA SECTION ========== */}
      <section className="kids-fun-cta">
        <Container>
          <Row className="align-items-center text-center text-lg-start">
            <Col lg={8}>
              <h2>Ready to Start Your Child's Tech Journey? 🚀</h2>
              <p>
                Join 500+ happy young learners who are building amazing things
                with code!
              </p>
              <div className="kids-fun-cta-buttons">
                <Button
                  className="kids-fun-cta-primary"
                  onClick={() => navigate("/register")}
                >
                  Enroll Now ✨
                </Button>
                <Button
                  className="kids-fun-cta-secondary"
                  onClick={() => setShowVideoModal(true)}
                >
                  Book Free Demo Class 📅
                </Button>
              </div>
            </Col>
            <Col lg={4}>
              <div className="kids-fun-cta-emoji">
                <span>🧒</span>
                <span>💻</span>
                <span>🤖</span>
                <span>🎮</span>
                <span>🏆</span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ========== VIDEO DEMO MODAL ========== */}
      <Modal
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        size="lg"
        centered
        className="kids-fun-video-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>🎬 Watch Demo - Kids Coding Program</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="kids-fun-video-container">
            <iframe
              width="100%"
              height="500"
              src="https://www.youtube.com/embed/QnubPziozsw?si=9QgZqywHtYmY0lw8&autoplay=1"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVideoModal(false)}>
            <FaTimes className="me-2" /> Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ========== PROJECT IMAGE MODAL ========== */}
      <Modal
        show={showProjectModal}
        onHide={() => setShowProjectModal(false)}
        size="lg"
        centered
        className="kids-fun-project-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedProject?.title} - Student Project</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="kids-fun-project-image-container">
            {selectedProject?.image ? (
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="kids-fun-project-image"
              />
            ) : (
              <div className="kids-fun-project-placeholder">
                <span className="kids-fun-project-icon">
                  {selectedProject?.icon}
                </span>
                <h3>{selectedProject?.title}</h3>
                <p>{selectedProject?.description}</p>
                <p className="text-muted">⭐ {selectedProject?.student}</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="kids-fun-project-info">
            <span>👨‍🎓 Created by: {selectedProject?.student}</span>
            <span>📂 Category: {selectedProject?.category}</span>
          </div>
          <Button
            variant="secondary"
            onClick={() => setShowProjectModal(false)}
          >
            <FaTimes className="me-2" /> Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </div>
  );
};

export default Kids;
